import { formatNumber } from '@angular/common';
import { Injectable, Renderer2 } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  AskBidMidNumber,
  Leg,
  PricingLatestValuesRequests,
  RfqLegSettings
} from '@approot/models';
import {
  ConfigService,
  LegSyncService,
  MarketDataService,
  PricingExtraService,
  ProductsConfigService,
  SpotResponse,
  StrikePayload,
  StrikeResponse
} from '@approot/services';
import { reformatDate, Utilities } from '@approot/utilities';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  addTooltipIfExpired,
  fieldNumber,
  getColorIfExpired,
  isGettingALatestStrike,
  makeTextCustomColor,
  markFieldAsPending,
  removePendingFromField,
  setBracketedAttributeInfo,
  setCalculatedField,
  stringOrFormControlToFormControl
} from '../utilities/leg.utilities';
import {
  counterNotionalAfterValuesSet,
  notionalChangeNoStrikeReCalc
} from '../utilities/notional-utilities';
import { strikePayload } from '../utilities/payload-creation';
import { LegFwdVolService } from './leg-fwd-vol.service';
import { LegHelperService } from './leg-helper.service';

@Injectable({
  providedIn: 'root'
})
export class LegSpotStrikeService {
  public spotChangesSubscription: Subscription;
  public hasSpotAlreadyLoaded = false;
  public hasSecondSpotAlreadyLoaded = false;
  constructor(
    private legHelperService: LegHelperService,
    private legSyncService: LegSyncService,
    private productsConfigService: ProductsConfigService,
    private marketDataService: MarketDataService,
    private legFwdVolService: LegFwdVolService,
    private pricingExtraService: PricingExtraService,
    private configService: ConfigService,
    private renderer: Renderer2
  ) {}

  public removeAllStrikePending(strikeNumber): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;

    [
      `strike${strikeNumber}`,
      `vol${strikeNumber}Bid`,
      `vol${strikeNumber}Ask`,
      'forwardPointsBid',
      'forwardPointsAsk'
    ].forEach(key => {
      removePendingFromField(form, leg, rfqExecutionSettings, key);
    });
  }

  public updateSpot(value: string, manual = false): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const cross = form.get('cross')?.value || leg.usableCross;
    if (!cross) {
      return;
    }
    this.legSyncService.updateSpot({
      spot: value,
      srcLeg: this.legHelperService.legIndex,
      cross,
      manual
    });
  }

  handleStrikeSpotFormat(
    field: string | FormControl,
    value: number,
    shouldEmit: boolean,
    cross: string,
    resp: { valueDateTime: string; displayDate?: string },
    emitChangeInLeg: boolean,
    spotDate: string
  ) {
    // doNotEmit indicates whether we want the form control to emit a value.
    // this is because emitting a value results in the price being cleared
    // which we don't want to happen in the cases of auto refreshing.

    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const decimalPlacesFormat =
      this.productsConfigService.decimalPlacesForCrossPipe(cross);
    const dateField =
      field === 'spot' || field === 'spotAsk' || field === 'spotBid'
        ? 'spotDate'
        : `${field}Date`;
    const control = stringOrFormControlToFormControl(field, form);
    const dateControl = stringOrFormControlToFormControl(dateField, form);
    if (dateControl && resp.displayDate) {
      const xccySwapStartDate = form.get('xccySwapStartDate');
      if (xccySwapStartDate && !xccySwapStartDate.valid) {
        xccySwapStartDate.setValue(reformatDate(spotDate, 'D MMM YYYY'), {
          emitEvent: false
        });
        xccySwapStartDate.setErrors(null);
      }
      dateControl.setValue(resp.displayDate, false);
    }

    const valueStr = formatNumber(value, 'en-GB', decimalPlacesFormat).replace(
      /,/g,
      ''
    );
    control.setValue(valueStr, { emitEvent: shouldEmit });

    if (this.legHelperService.legSyncingInfo?.spot) {
      leg.manuallyEnteredSpot = false;
      this.updateSpot(valueStr);
    }
    // we should set this to false

    if (
      field === 'spot' ||
      field === 'spotAsk' ||
      field === 'spotBid' ||
      field === 'secondSpot'
    ) {
      // since the spot changed from a service call
      // indicate that we need to get the latest spot when updating
      leg.usingLatestValues[field] = true;
      leg.usingLatestValues.spotDate =
        field === 'spot' || field === 'spotAsk' || field === 'spotBid';
      makeTextCustomColor(
        field,
        getColorIfExpired(resp) || Utilities.whiteTextColor,
        leg
      );
      addTooltipIfExpired(field, resp.valueDateTime, leg);
    }

    if (emitChangeInLeg) {
      this.legHelperService.onChangeInLegFn();
    }
  }

  public strikeCalculator(
    payload: StrikePayload,
    strikeNumber: string,
    shouldEmit: boolean
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const strike = `strike${strikeNumber}`;
    const volsAndFwdsExpected = payload.task.expiryDate;
    markFieldAsPending(form, leg, strike);
    if (volsAndFwdsExpected) {
      markFieldAsPending(form, leg, `vol${strikeNumber}Bid`);
      markFieldAsPending(form, leg, `vol${strikeNumber}Ask`);
      markFieldAsPending(form, leg, 'forwardPointsBid');
      markFieldAsPending(form, leg, 'forwardPointsAsk');
    }
    this.strikeCalculator$(payload, strike, shouldEmit).subscribe();
  }

  public strikeCalculator$(
    payload: StrikePayload,
    strike: string,
    shouldEmit: boolean,
    isInPricingMode = false
  ): Observable<StrikeResponse> {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const strikeNumber = fieldNumber(strike);
    return this.marketDataService.calculateStrike(payload).pipe(
      tap((resp: StrikeResponse) => {
        if (!isInPricingMode) {
          setBracketedAttributeInfo(strike, leg.shortcutValueUsed[strike], leg);
        }
        removePendingFromField(form, leg, rfqExecutionSettings, strike); //hide spinner
        makeTextCustomColor(strike, Utilities.whiteTextColor, leg);

        this.handleStrikeSpotFormat(
          strike,
          resp.strike,
          shouldEmit,
          payload.task.cross,
          { valueDateTime: resp.timestamp },
          true,
          null
        );

        this.legFwdVolService.handleVolsFromStrike(
          resp.strikeVol,
          resp.timestamp,
          isInPricingMode,
          strikeNumber
        );

        this.legFwdVolService.handleFwdsFromStrike(
          resp.forwardPoints,
          resp.timestamp,
          isInPricingMode
        );

        if (isInPricingMode) {
          this.removeAllStrikePending(strikeNumber);
        }
        this.setNotionalOnStrikeChange(strikeNumber);
      })
    );
  }

  public setNotionalOnStrikeChange(
    strikeNumber: string,
    strikeReCalc = true
  ): void {
    // sets the value into the strike field, and updates the notional currency
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const fieldsToBeEdited = this.legHelperService.fieldsToBeEdited;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const counterNotional = form.get(
      'counterNotional' + strikeNumber
    ) as FormControl;
    const fieldToReplace =
      fieldsToBeEdited['notional' + strikeNumber] || 'counterNotional'; // field that will be replaced
    // either notional or counterNotional
    const fieldToReplaceControl = form.get(fieldToReplace + strikeNumber);
    if (
      form.get('strike' + strikeNumber) &&
      form.get('notional' + strikeNumber) &&
      (counterNotional || rfqExecutionSettings.directRfq)
    ) {
      const notional = Utilities.notionalCurrencyRemover(
        form.get('notional' + strikeNumber).value
      );
      const strikeValue = getStrikeValue(form.get('strike' + strikeNumber));
      if (strikeReCalc) {
        this.onStrikeCalc(strikeNumber); // convert the strike special values into a number
      }
      if (!isNaN(strikeValue) && strikeValue !== '' && fieldToReplaceControl) {
        fieldToReplaceControl.setValue(
          setCalculatedField(
            fieldToReplace === 'notional'
              ? Utilities.notionalCurrencyRemover(counterNotional.value)
              : notional,
            strikeValue,
            fieldToReplace === 'notional' ? '/' : '*'
          ),
          { emitEvent: false }
        );

        form.get(fieldToReplace + strikeNumber).setErrors(null); // there shouldn't be any errors

        // we want to update the notional/counterNotional text state (e.g. add the currency at the end)
        fieldToReplace === 'notional'
          ? notionalChangeNoStrikeReCalc(
              strikeNumber,
              form,
              leg,
              rfqExecutionSettings,
              this.legHelperService.qs('#' + 'notional' + strikeNumber),
              this.renderer
            )
          : counterNotionalAfterValuesSet(
              strikeNumber,
              form,
              leg,
              this.legHelperService.qs('#' + 'counterNotional' + strikeNumber),
              this.renderer
            );
      }
    }
  }

  public onStrikeCalc(strikeNumber: string, useDeltaValue?: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const strikeField = `strike${strikeNumber}`;
    const strikeCtrl = form.get(strikeField);

    if (!this.legHelperService.presetsLoaded && leg.fromTab) {
      return;
    }

    if (strikeCtrl?.valid || useDeltaValue) {
      const strikeValue =
        useDeltaValue || getStrikeValue(strikeCtrl).toString().toLowerCase();
      const timestamp = this.pricingExtraService.getMarketDataTimeStamp;

      const payload = strikePayload(
        form,
        strikeNumber,
        this.productsConfigService.productFormatter(form.value, leg.attributes),
        strikeValue,
        timestamp
      );

      if (payload) {
        leg.usingLatestValues[strikeField] = true;
        leg.shortcutValueUsed[strikeField] = strikeValue;
        this.strikeCalculator(payload, strikeNumber, true); // calculate the strike with the payload
      }
    }
  }

  public isStrikeValid(fieldName): boolean {
    const form = this.legHelperService.form;
    return !form.get(fieldName) || !isNaN(form.get(fieldName).value);
  }

  public handleSpotOnPricing(
    key: 'spot' | 'secondSpot',
    resp: AskBidMidNumber,
    timestamp: string,
    displayDate: string,
    spotDate: string
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (resp && leg.usingLatestValues[key]) {
      let cross = form.get(key === 'spot' ? 'cross' : 'secondCross')?.value;
      if (!cross) {
        const c1 = form.get('depositCurrency').value;
        const c2 = form.get('alternativeCurrency').value;
        if (c1 && c2) {
          cross = this.productsConfigService.getCrossFromCurrencies(c1, c2);
        }
      }
      if (cross) {
        this.handleStrikeSpotFormat(
          key,
          resp.mid,
          false,
          cross,
          { valueDateTime: timestamp, displayDate },
          false,
          spotDate
        ); // do not emit an event
      }
    }
  }

  public addRequestsForLatestsSpots(
    liveValueRequests: PricingLatestValuesRequests
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    let cross = this.legHelperService.valueToUseAsCross(false);
    if (leg.usingLatestValues.spot && cross && !isGettingALatestStrike(leg)) {
      // if there is a spot that was created with a spot call and a valid cross
      liveValueRequests.spot = this.marketDataService
        .calculateSpot({
          cross,
          timestamp: this.pricingExtraService.getMarketDataTimeStamp
        })
        .pipe(
          tap((resp: SpotResponse) => {
            this.handleSpotOnPricing(
              'spot',
              resp.spot,
              resp.timestamp,
              resp.displayedSpotDate,
              resp.spotDate
            );
          })
        );
    }

    if (leg.usingLatestValues.secondSpot) {
      // if there is a second spot that was created with a spot call update the value
      liveValueRequests.secondSpot = this.marketDataService
        .calculateSpot({
          cross: form.get('secondCross').value,
          timestamp: this.pricingExtraService.getMarketDataTimeStamp
        })
        .pipe(
          tap((resp: SpotResponse) => {
            this.handleSpotOnPricing(
              'secondSpot',
              resp.spot,
              resp.timestamp,
              resp.displayedSpotDate,
              null
            );
          })
        );
    }
  }

  public addShortcutStrikesRequest(
    requests: PricingLatestValuesRequests
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    Object.keys(leg.shortcutValueUsed).forEach(key => {
      if (leg.shortcutValueUsed[key]) {
        const timestamp = this.pricingExtraService.getMarketDataTimeStamp;
        const payload = strikePayload(
          form,
          fieldNumber(key),
          this.productsConfigService.productFormatter(
            form.value,
            leg.attributes
          ),
          leg.shortcutValueUsed[key],
          timestamp
        );
        requests[key] = this.strikeCalculator$(payload, key, false, true).pipe(
          tap(strikeResp => {
            this.handleSpotOnPricing(
              'spot',
              strikeResp.spot,
              strikeResp.timestamp,
              null,
              null
            );
          })
        );
      }
    });
  }

  public spotCaller(
    cross: string,
    field: string | FormControl,
    emitEvent: boolean,
    bothSides = false
  ): void {
    // when the spot is blurred send a request for the latest spot from the server
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (
      typeof field === 'string' &&
      !this.legHelperService.presetsLoaded &&
      leg.preSetPosition?.[field] != null
    ) {
      return;
    }
    const formCtrl = stringOrFormControlToFormControl(field, form);

    if (!formCtrl) {
      return;
    }

    const fields = [field];

    if (
      field === 'spotAsk' &&
      (!stringOrFormControlToFormControl('spotBid', form).value || bothSides)
    ) {
      fields.push('spotBid');
    } else if (
      field === 'spotBid' &&
      (!stringOrFormControlToFormControl('spotAsk', form).value || bothSides)
    ) {
      fields.push('spotAsk');
    }

    fields.forEach(keyField => {
      markFieldAsPending(form, leg, keyField);
    });

    if (field === 'spot' || field === 'spotAsk' || field === 'spotBid') {
      markFieldAsPending(form, leg, 'spotDate');
    }
    this.marketDataService
      .calculateSpot({
        cross,
        timestamp: this.pricingExtraService.getMarketDataTimeStamp
      })
      .subscribe({
        next: (resp: SpotResponse) => {
          fields.forEach(keyField => {
            let val = resp.spot.mid;
            if (keyField === 'spotAsk') {
              val = resp.spot.ask;
            } else if (keyField === 'spotBid') {
              val = resp.spot.bid;
            }

            this.handleStrikeSpotFormat(
              keyField,
              val,
              emitEvent,
              cross,
              {
                valueDateTime: resp.timestamp,
                displayDate: resp.displayedSpotDate
              },
              true,
              resp.spotDate
            );
          });

          this.removeStrikeSpotPending(
            fields,
            form,
            leg,
            rfqExecutionSettings,
            field
          );
        },
        error: () => {
          this.removeStrikeSpotPending(
            fields,
            form,
            leg,
            rfqExecutionSettings,
            field
          );
        }
      });
  }

  private removeStrikeSpotPending(
    fields: (string | FormControl)[],
    form: FormGroup<any>,
    leg: Leg,
    rfqExecutionSettings: RfqLegSettings,
    field: string | FormControl<any>
  ): void {
    fields.forEach(keyField => {
      removePendingFromField(form, leg, rfqExecutionSettings, keyField);
    });
    if (field === 'spot' || field === 'spotAsk' || field === 'spotBid') {
      removePendingFromField(form, leg, rfqExecutionSettings, 'spotDate');
    }
  }

  public onSpotChange(count: number, spot: string): void {
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    this.onStrikeCalc(''); // convert the strike special values into a number
    this.onStrikeCalc('1');
    this.onStrikeCalc('2');
    this.manuallyEnteredSpot = count > 1;
    if (legSyncingInfo?.spot) {
      this.updateSpot(spot, this.manuallyEnteredSpot);
    }
  }

  public get manuallyEnteredSpot(): boolean {
    const leg = this.legHelperService.leg;
    return leg.manuallyEnteredSpot;
  }

  public set manuallyEnteredSpot(manual: boolean) {
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    const leg = this.legHelperService.leg;
    if (!this.legHelperService.presetsLoaded && legSyncingInfo?.spot) {
      return;
    }
    leg.manuallyEnteredSpot = manual;
  }

  public subToSpotChanges(legIndex: number): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const cross = form.get('cross')?.value || leg.usableCross;
    if (!cross) {
      return;
    }
    this.spotChangesSubscription?.unsubscribe();
    this.spotChangesSubscription = this.legSyncService
      .subscribeToSpotChanges(cross, legIndex)
      .subscribe(spotData => {
        const ctrl = form.get('spot');
        const curr = ctrl.value;
        const spot = spotData.spot;
        if (curr !== spot) {
          // pending stops the field from being flashed
          ctrl.markAsPending();
          ctrl.setValue(spot);
        }
        this.manuallyEnteredSpot = spotData.manual;
      });
  }

  /**
   * In RFQs, strikes can be formatted with reduced precision.
   */
  public reformatStrikeforRFQ(attr: string, leg: Leg): void {
    let oldVal: string = leg.preSetPosition?.[attr]
      ?.toString()
      .replace(/,/g, '');
    if (!oldVal) {
      return;
    }
    const cross = leg.preSetPosition.cross;
    const currencyPairs = this.configService.userSettings().currencyPairs;
    const newDPs = currencyPairs.find(cp => cp.currencyPair === cross)
      ?.rfqStrikePrecision;
    if (newDPs == null) {
      return;
    }

    let newVal: string;
    if (oldVal === Utilities.customText) {
      newVal = oldVal;
    } else {
      newVal = (+oldVal).toFixed(newDPs);
    }
    leg.preSetPosition[attr] = newVal;
    // Old and new values should be numerically the same
    if (+oldVal !== +newVal || newVal === Utilities.customText) {
      makeTextCustomColor(attr, Utilities.orangeTextColor, leg);
    }
  }

  public syncSpotsListener(legIndex: number) {
    if (!this.legHelperService.legSyncingInfo?.spot) {
      return;
    }
    // TODO move into customFormListeners

    this.subToSpotChanges(legIndex);
    this.legHelperService.subSink.sink = this.spotChangesSubscription;
  }
}

export function getStrikeValue(ctrl: AbstractControl): any {
  if (Utilities.isNumberFormatted(ctrl.value, true)) {
    return Utilities.formattedNumberToNumber(ctrl.value);
  }
  return ctrl.value;
}
